import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import AppLayout from '../layouts/AppLayout';
import useAuth from '../hooks/useAuth';

/**
 * routes.jsx
 *
 * Centralised React Router v6 route tree for the Axial rebrand.
 *
 * Strategy:
 *  - All authenticated routes are children of AppLayout (sidebar + top nav).
 *  - Public/unauthenticated routes use PublicLayout.
 *  - Lazy-loaded page components reduce initial bundle size.
 *  - Route paths are defined as constants in ROUTES below to avoid magic strings.
 *
 * TODO (axial-rebrand migration):
 *  - Replace placeholder page components with real migrated feature pages.
 *  - Add ProtectedRoute wrapper that redirects unauthenticated users to /login.
 *  - Add role guards for /admin using a RequireRole wrapper.
 */

function isInviteFlowLocation(location) {
  const params = new URLSearchParams(location?.search || '');
  const hashParams = new URLSearchParams((location?.hash || '').replace(/^#/, ''));
  const queryType = params.get('type');
  const hashType = hashParams.get('type');
  const queryHasInviteTokens = Boolean(params.get('token_hash') || params.get('token'));
  const hashHasInviteTokens = Boolean(hashParams.get('token_hash') || hashParams.get('token'));
  const queryHasSessionTokens = Boolean(params.get('access_token') && params.get('refresh_token'));
  const hashHasSessionTokens = Boolean(hashParams.get('access_token') && hashParams.get('refresh_token'));

  return (
    (queryType === 'invite' && (queryHasInviteTokens || queryHasSessionTokens)) ||
    (hashType === 'invite' && (hashHasInviteTokens || hashHasSessionTokens))
  );
}

function debugRoutes(label, payload) {
  if (process.env.REACT_APP_RUNTIME_PHASE !== 'testing') {
    return;
  }

  console.log(`[routes] ${label}`, payload);
}

// ── Route path constants ────────────────────────────────────────────────────
export const ROUTES = {
  ROOT:          '/',
  LOGIN:         '/auth',
  LOGIN_ALIAS:   '/login',
  DASHBOARD:     '/dashboard',
  PROJECTS:      '/projects',
  PROJECT:       '/projects/:projectId',
  TASKS:         '/tasks',
  REVIEWS:       '/reviews',
  DRAWINGS:      '/drawings',
  DRAWING:       '/drawings/:drawingId',
  TEAMS:         '/teams',
  TEAM:          '/team',
  PERMITS:        '/permits',
  PERMIT_EDIT:    '/permits/:permitId',
  PERMIT_PDF_REVIEW: '/permits/:permitId/pdf-review/:documentId',
  AUDIT:          '/audit',
  TRANSMITTALS:  '/transmittals',
  REVISIONS:     '/revisions',
  QA:            '/qa',
  QA_CHECKLIST:  '/qa/:checklistId',
  NOTIFICATIONS: '/notifications',
  REPORTS:       '/reports',
  SETTINGS:      '/settings',
  ADMIN:         '/admin',
  SHARE:         '/public-share/:token',
  REQUEST_PASSWORD_RESET: '/request-password-reset',
  RESET_PASSWORD: '/reset-password',
  ONBOARDING: '/onboarding',
  SIGNUP: '/signup',
  PENDING_ASSIGNMENT: '/pending-assignment',
};

function getPageTitle(pathname) {
  const pathSegments = pathname.split('/').filter(Boolean);
  const routeTitles = {
    dashboard: 'Dashboard',
    projects: 'Projects',
    tasks: 'Tasks',
    reviews: 'Reviews',
    transmittals: 'Transmittals',
    drawings: 'Drawings',
    teams: 'Teams',
    qa: 'Quality Assurance',
    notifications: 'Notifications',
    reports: 'Reports',
    settings: 'Settings',
    permits: 'Permits',
    audit: 'Audit Log',
    admin: 'Administration',
    auth: 'Sign In',
    login: 'Sign In',
    'request-password-reset': 'Reset Password',
    'reset-password': 'Reset Password',
    onboarding: 'Create Account',
    signup: 'Create Account',
    'pending-assignment': 'Account Assignment',
    'public-share': 'Shared Content',
  };
  const section = routeTitles[pathSegments[0]];
  let pageTitle = section || 'Page Not Found';

  if (pathSegments[0] === 'projects' && pathSegments[1]) {
    pageTitle = `Project ${pathSegments[1]}`;
  } else if (pathSegments[0] === 'drawings' && pathSegments[1]) {
    pageTitle = `Drawing ${pathSegments[1]}`;
  } else if (pathSegments[0] === 'permits' && pathSegments[1]) {
    pageTitle = pathSegments[2] === 'pdf-review'
      ? `Permit PDF Review ${pathSegments[1]}`
      : `Permit ${pathSegments[1]}`;
  } else if (pathSegments[0] === 'qa' && pathSegments[1]) {
    pageTitle = `Checklist ${pathSegments[1]}`;
  }

  return `${pageTitle} | Axial`;
}

// ── Lazy page imports ────────────────────────────────────────────────────────
// Fallback to existing pages until migrated into features/
const DashboardPage     = lazy(() => import('../pages/DashboardPage'));
const ProjectsPage      = lazy(() => import('../features/projects/ProjectsPage'));
const DrawingsPage      = lazy(() => import('../features/drawings/DrawingsPage'));
const TeamsPage         = lazy(() => import('../features/teams/TeamsPage'));
const NotificationsPage = lazy(() => import('../features/notifications/NotificationsPage'));
const AdminPage         = lazy(() => import('../features/admin/AdminPage'));
const TasksPage         = lazy(() => import('../features/tasks/TasksPage'));
const ReviewsPage       = lazy(() => import('../features/reviews/ReviewsPage'));
const TransmittalsPage  = lazy(() => import('../features/transmittals/TransmittalPage'));
const PermitsPage       = lazy(() => import('../features/permits/PermitsPage'));
const PermitEditPage    = lazy(() => import('../features/permits/PermitEdit'));
const PermitPdfReview   = lazy(() => import('../features/permits/PermitPdfReview'));
const AuditPage         = lazy(() => import('../features/audit/AuditPage'));
// Existing pages (not yet migrated)
const QAHome            = lazy(() => import('../features/reviews/qa/QAHome'));
const Reports           = lazy(() => import('../pages/Reports'));
const SettingsPage      = lazy(() => import('../features/settings/SettingsPage'));
const AuthRouter        = lazy(() => import('../AuthRouter'));
const PasswordResetRequest = lazy(() => import('../pages/PasswordResetRequest'));
const ResetPassword     = lazy(() => import('../pages/ResetPassword'));
const Onboarding        = lazy(() => import('../pages/Onboarding'));
const PendingAssignment = lazy(() => import('../pages/PendingAssignment'));
const PublicSharePage   = lazy(() => import('../pages/PublicSharePage'));
const NotFound          = lazy(() => import('../pages/NotFound'));

// ── Loading fallback ─────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
      Loading…
    </div>
  );
}

// ── Route tree ───────────────────────────────────────────────────────────────
function AppRoutes() {
  const location = useLocation();
  const inviteFlow = isInviteFlowLocation(location);

  React.useEffect(() => {
    document.title = getPageTitle(location.pathname);
  }, [location.pathname]);

  const rootRedirectTarget = inviteFlow
    ? { pathname: ROUTES.SIGNUP, search: location.search, hash: location.hash }
    : { pathname: ROUTES.DASHBOARD };

  React.useEffect(() => {
    debugRoutes('root-redirect-target', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      inviteFlow,
      redirectPathname: rootRedirectTarget.pathname,
      redirectSearch: rootRedirectTarget.search ?? '',
      redirectHash: rootRedirectTarget.hash ?? '',
    });
  }, [inviteFlow, location.hash, location.pathname, location.search, rootRedirectTarget.hash, rootRedirectTarget.pathname, rootRedirectTarget.search]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root redirect */}
        <Route path={ROUTES.ROOT} element={<Navigate to={rootRedirectTarget} replace />} />

        {/* Public / utility routes retained for parity */}
        <Route path={ROUTES.LOGIN} element={<AuthRouter />} />
        <Route path={ROUTES.LOGIN_ALIAS} element={<Navigate to={ROUTES.LOGIN} replace />} />
        <Route path={ROUTES.REQUEST_PASSWORD_RESET} element={<PasswordResetRequest />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={ROUTES.ONBOARDING} element={<Onboarding />} />
        <Route path={ROUTES.SIGNUP} element={<Onboarding />} />
        <Route path={ROUTES.PENDING_ASSIGNMENT} element={<PendingAssignment />} />
        <Route path={ROUTES.SHARE} element={<PublicSharePage />} />

        {/* Authenticated app routes */}
        <Route element={<RequireAuth />}>
          <Route path={ROUTES.DASHBOARD}     element={<DashboardPage />} />
          <Route path={ROUTES.PROJECTS}      element={<ProjectsPage />} />
          <Route path={ROUTES.PROJECT}       element={<ProjectsPage />} />
          <Route path={ROUTES.TASKS}         element={<TasksPage />} />
          <Route path={ROUTES.REVIEWS}       element={<ReviewsPage />} />
          <Route path={ROUTES.TRANSMITTALS}  element={<TransmittalsPage />} />
          <Route path={ROUTES.DRAWINGS}      element={<DrawingsPage />} />
          <Route path={ROUTES.DRAWING}       element={<DrawingsPage />} />
          <Route path={ROUTES.TEAM}          element={<Navigate to={ROUTES.TEAMS} replace />} />
          <Route path={ROUTES.TEAMS}         element={<TeamsPage />} />
          <Route path={ROUTES.REVISIONS}     element={<ReviewsPage />} />
          <Route path={ROUTES.QA}            element={<QAHome />} />
          <Route path={ROUTES.QA_CHECKLIST}  element={<QAHome />} />
          <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
          <Route path={ROUTES.REPORTS}       element={<Reports />} />
          <Route path={ROUTES.SETTINGS}      element={<SettingsPage />} />
          <Route path={ROUTES.PERMITS}       element={<PermitsPage />} />
          <Route path={ROUTES.PERMIT_EDIT}   element={<PermitEditPage />} />
          <Route path={ROUTES.PERMIT_PDF_REVIEW} element={<PermitPdfReview />} />
          <Route path={ROUTES.AUDIT}         element={<AuditPage />} />
          <Route path={ROUTES.ADMIN}         element={<AdminPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function RequireAuth() {
  const { user, isLoading, isProfileLoading, authFlowState } = useAuth();
  const location = useLocation();

  if (isLoading || isProfileLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location.pathname }} />;
  }

  if (authFlowState === 'onboarding') {
    return <Navigate to={ROUTES.SIGNUP} replace state={{ from: location.pathname }} />;
  }

  if (authFlowState === 'pending-assignment') {
    return <Navigate to={ROUTES.PENDING_ASSIGNMENT} replace state={{ from: location.pathname }} />;
  }

  return <AppLayoutWrapper />;
}

/**
 * Thin wrapper that renders AppLayout with managed sidebar state.
 * Uses React Router's <Outlet /> to render matched child routes.
 */
function AppLayoutWrapper() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    const savedValue = window.localStorage.getItem('axial.sidebar.open');
    return savedValue == null ? true : savedValue === 'true';
  });

  const isDrawingsRoute =
    location.pathname === ROUTES.DRAWINGS || location.pathname.startsWith(`${ROUTES.DRAWINGS}/`);

  React.useEffect(() => {
    window.localStorage.setItem('axial.sidebar.open', String(sidebarOpen));
  }, [sidebarOpen]);

  return (
    <AppLayout
      sidebarOpen={sidebarOpen}
      onSidebarToggle={() => setSidebarOpen(v => !v)}
      contentNoPadding={isDrawingsRoute}
      contentMaxWidth={isDrawingsRoute ? 'max-w-none' : undefined}
        contentScrollable={!isDrawingsRoute}
      contentClassName={isDrawingsRoute ? 'h-full' : ''}
    >
      <Outlet />
    </AppLayout>
  );
}

export default AppRoutes;
